import React from "react";
import { StepProps } from "../../types";
import { useWebTranslation } from "translations";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import { Typography } from "ui-kit/Typography";
import { useSignupStore } from "components/Signup/store/hooks";
import { selectLiabilityCancelledByPreviousCompany } from "components/Signup/store/selectors/insurance";
import { useHasLiabilityAddon } from "components/Signup/store/selectors/animals";
import {
  removeAllLiabilityAddons,
  updateInsurancesOfType,
} from "components/Signup/store/slices/animals";
import { Radio } from "ui-kit/Radio";
import Gutter from "ui-kit/Gutter";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { useIsLoading } from "components/Signup/store/selectors/metadata";
import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";
import { Button } from "ui-kit/Button";

const LiabilityCancelledPreviouslyQuestion = ({
  next,
  previous,
  isValid,
  transitioning,
}: StepProps) => {
  const { t } = useWebTranslation();
  const { dispatch, getState } = useSignupStore();
  const isLoading = useIsLoading();
  const hasLiabilityAddon = useHasLiabilityAddon();

  const liabilityCancelledByPreviousInsuranceCompany =
    selectLiabilityCancelledByPreviousCompany(getState());

  const isNotElibleForLiabilityAddon =
    hasLiabilityAddon && liabilityCancelledByPreviousInsuranceCompany === "YES";

  return (
    <ChatContainer leaving={transitioning}>
      <ChatMessage>
        <Typography>
          {t("policy_previously_cancelled.germany.liability.question.title")}
        </Typography>
      </ChatMessage>
      <ChatMessage
        fromMe
        index={2}
        onPress={() => {
          dispatch(
            updateInsurancesOfType({
              type: SignUpProductEnum.liability,
              update: {
                liabilityCancelledByPreviousInsuranceCompany: "NO",
              },
            })
          );
        }}
      >
        <Radio active={liabilityCancelledByPreviousInsuranceCompany === "NO"}>
          <Typography>{t("global.no")}</Typography>
        </Radio>
      </ChatMessage>
      <ChatMessage
        fromMe
        index={2}
        onPress={() => {
          dispatch(
            updateInsurancesOfType({
              type: SignUpProductEnum.liability,
              update: {
                liabilityCancelledByPreviousInsuranceCompany: "YES",
              },
            })
          );
        }}
      >
        <Radio active={liabilityCancelledByPreviousInsuranceCompany === "YES"}>
          <Typography>{t("global.yes")}</Typography>
        </Radio>
      </ChatMessage>

      <Gutter size={2} />
      {liabilityCancelledByPreviousInsuranceCompany === "YES" && (
        <ErrorMessage>
          <Typography color={"error"}>
            <div aria-live="polite" role="alert">
              {t(
                "policy_previously_cancelled.germany.liability.question.yes_answer"
              )}
            </div>
          </Typography>
        </ErrorMessage>
      )}
      {isNotElibleForLiabilityAddon ? (
        <>
          <Gutter size={2} />
          <Button
            onPress={() => {
              dispatch(removeAllLiabilityAddons());
              next(true, "user-info");
            }}
            title={t("signup.continue_without_liability")}
          />
        </>
      ) : (
        <FlowButtons
          inProgress={transitioning || isLoading}
          backButton={false}
          next={() => next(hasLiabilityAddon)}
          disabled={!isValid}
          previous={previous}
        />
      )}
    </ChatContainer>
  );
};

export default LiabilityCancelledPreviouslyQuestion;
